import { useEffect, useMemo, useState } from "react";
import {
  Link2,
  Loader2,
  Copy,
  ExternalLink,
  RefreshCw,
  Trash2,
  Search,
  FileText,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { API } from "@/lib/api";

const DEFAULT_FORM = {
  amount: "",
  currency: "INR",
  description: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  reference_id: "",
  invoice_id: "",
  expire_days: "",
};

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  partially_paid: "bg-amber-100 text-amber-700 border-amber-200",
  created: "bg-blue-100 text-blue-700 border-blue-200",
  issued: "bg-blue-100 text-blue-700 border-blue-200",
  expired: "bg-slate-200 text-slate-600 border-slate-300",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

const DEFAULT_PHONE_COUNTRY_CODE = "+91";

const normalizePhoneForApi = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return `+${digits}`;
  if (digits.length === 10) return `${DEFAULT_PHONE_COUNTRY_CODE}${digits}`;
  if ((digits.length === 11 || digits.length === 12) && digits.startsWith("91")) {
    return `+${digits}`;
  }
  return `+${digits}`;
};

const isSafeHttpsUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:";
  } catch {
    return false;
  }
};

const formatAmount = (amount, currency = "INR") => {
  const value = Number(amount || 0);
  if (currency === "INR") {
    return `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${currency} ${value.toFixed(2)}`;
};

export const RazorpayLinks = () => {
  const [tab, setTab] = useState("create");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [search, setSearch] = useState("");
  const [invoiceLookup, setInvoiceLookup] = useState("");
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const updateField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API}/razorpay/config`);
      const data = await res.json();
      setConfigured(Boolean(data?.configured));
    } catch (err) {
      console.error("Failed to load Razorpay config", err);
    }
  };

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await fetch(`${API}/razorpay/links`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Unable to load payment links");
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchLinks();
  }, []);

  const handleLoadFromInvoice = async () => {
    const query = invoiceLookup.trim();
    if (!query) {
      toast.error("Enter an invoice number or phone to prefill");
      return;
    }
    setLoadingInvoice(true);
    try {
      const res = await fetch(`${API}/invoices?search=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error(await res.text());
      const list = await res.json();
      if (!Array.isArray(list) || list.length === 0) {
        toast.error("No invoice found for that search");
        return;
      }
      const invoice = list[0];
      setForm((prev) => ({
        ...prev,
        amount: invoice.total_payable ? String(invoice.total_payable) : prev.amount,
        description:
          invoice.invoice_number
            ? `Invoice ${invoice.invoice_number} - Pokus Technologies`
            : prev.description,
        customer_name: invoice.customer_name || prev.customer_name,
        customer_phone: invoice.phone || prev.customer_phone,
        reference_id: invoice.invoice_number || prev.reference_id,
        invoice_id: invoice.id || prev.invoice_id,
      }));
      toast.success(`Prefilled from invoice ${invoice.invoice_number}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoice details");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const phone = normalizePhoneForApi(form.customer_phone);
    const rawDigits = (form.customer_phone || "").replace(/\D/g, "");
    if (!phone || rawDigits.length < 10) {
      toast.error("Enter a valid contact number (10 digits)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount,
        currency: form.currency || "INR",
        description: form.description,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: phone,
        reference_id: form.reference_id,
        invoice_id: form.invoice_id,
      };
      const days = parseInt(form.expire_days, 10);
      if (!Number.isNaN(days) && days > 0) {
        payload.expire_by = Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;
      }

      const res = await fetch(`${API}/razorpay/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create link");
      }
      const created = await res.json();
      toast.success("Payment link created");
      setLinks((prev) => [created, ...prev]);
      setForm(DEFAULT_FORM);
      setInvoiceLookup("");
      setTab("view");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to create payment link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (url) => {
    if (!isSafeHttpsUrl(url)) {
      toast.error("Link is not a valid https URL");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Unable to copy link");
    }
  };

  const handleRefresh = async (link) => {
    setRefreshingId(link.id);
    try {
      const res = await fetch(`${API}/razorpay/links/${link.id}/refresh`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Refresh failed");
      }
      const updated = await res.json();
      setLinks((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      toast.success("Status refreshed");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to refresh");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await fetch(`${API}/razorpay/links/refresh-all`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Bulk refresh failed");
      }
      const data = await res.json();
      if (Array.isArray(data.links)) {
        setLinks(data.links);
      }
      if (data.refreshed === 0) {
        toast.info("No pending links to refresh");
      } else if (data.failed > 0) {
        toast.warning(
          `Refreshed ${data.refreshed}, ${data.updated} updated, ${data.failed} failed`
        );
      } else {
        toast.success(
          `Refreshed ${data.refreshed} link${data.refreshed === 1 ? "" : "s"}` +
            (data.updated ? ` · ${data.updated} changed` : "")
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to refresh statuses");
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Remove this link from history?\n${link.short_url}`)) return;
    try {
      const res = await fetch(`${API}/razorpay/links/${link.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await res.text());
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
      toast.success("Link removed from history");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete entry");
    }
  };

  const pendingCount = useMemo(
    () =>
      links.filter((l) =>
        ["created", "issued", "partially_paid"].includes(l.status)
      ).length,
    [links]
  );

  const filteredLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) =>
      [
        l.customer_name,
        l.customer_phone,
        l.customer_email,
        l.description,
        l.reference_id,
        l.razorpay_id,
        l.short_url,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [links, search]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Razorpay Payment Links
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Generate and track Razorpay payment links with customer info pre-filled.
          </p>
        </div>
        <div
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border",
            configured
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-700 border-amber-200"
          )}
        >
          <span
            className={cn(
              "w-2 h-2 rounded-full",
              configured ? "bg-emerald-500" : "bg-amber-500"
            )}
          />
          {configured ? "Razorpay connected" : "Razorpay not configured"}
        </div>
      </div>

      {!configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex items-start gap-3 py-4 text-sm text-amber-800">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Missing credentials</p>
              <p className="mt-1">
                Set <code className="px-1 py-0.5 rounded bg-amber-100">RAZORPAY_KEY_ID</code>{" "}
                and{" "}
                <code className="px-1 py-0.5 rounded bg-amber-100">RAZORPAY_KEY_SECRET</code>{" "}
                in your backend environment to enable link creation.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-10">
          <TabsTrigger value="create" className="gap-2 px-4">
            <Plus className="w-4 h-4" /> Create Link
          </TabsTrigger>
          <TabsTrigger value="view" className="gap-2 px-4">
            <Link2 className="w-4 h-4" /> View Links
            <Badge variant="secondary" className="ml-1">
              {links.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Link details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50">
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Quick fill
                </Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Invoice number or customer phone"
                    value={invoiceLookup}
                    onChange={(e) => setInvoiceLookup(e.target.value)}
                    className="sm:max-w-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLoadFromInvoice}
                    disabled={loadingInvoice}
                    className="gap-2"
                  >
                    {loadingInvoice ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    Prefill from invoice
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Looks up the most recent matching invoice and fills in customer
                  name, phone, amount, and description.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={form.amount}
                      onChange={(e) => updateField("amount", e.target.value)}
                      placeholder="e.g. 1500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">Currency</Label>
                    <Input
                      id="currency"
                      value={form.currency}
                      onChange={(e) =>
                        updateField("currency", e.target.value.toUpperCase())
                      }
                      maxLength={3}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    placeholder="Invoice INV-1234 - Dry Cleaning"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Customer name</Label>
                    <Input
                      id="customer_name"
                      value={form.customer_name}
                      onChange={(e) =>
                        updateField("customer_name", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone">
                      Phone <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="customer_phone"
                      type="tel"
                      required
                      inputMode="numeric"
                      value={form.customer_phone}
                      onChange={(e) =>
                        updateField("customer_phone", e.target.value)
                      }
                      placeholder="9876543210"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      10-digit mobile is fine — country code {DEFAULT_PHONE_COUNTRY_CODE} is added automatically.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Email</Label>
                    <Input
                      id="customer_email"
                      type="email"
                      value={form.customer_email}
                      onChange={(e) =>
                        updateField("customer_email", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="reference_id">Reference ID</Label>
                    <Input
                      id="reference_id"
                      value={form.reference_id}
                      onChange={(e) =>
                        updateField("reference_id", e.target.value)
                      }
                      placeholder="Invoice # or internal ref"
                    />
                  </div>
                  <div>
                    <Label htmlFor="expire_days">Expire after (days)</Label>
                    <Input
                      id="expire_days"
                      type="number"
                      min="1"
                      value={form.expire_days}
                      onChange={(e) =>
                        updateField("expire_days", e.target.value)
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice_id">Linked invoice ID</Label>
                    <Input
                      id="invoice_id"
                      value={form.invoice_id}
                      onChange={(e) => updateField("invoice_id", e.target.value)}
                      placeholder="Auto-filled when prefilled"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setForm(DEFAULT_FORM)}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0047FF] hover:bg-[#0036cc] gap-2"
                    disabled={submitting || !configured}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Create payment link
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="view" className="mt-4">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">All payment links</CardTitle>
                {pendingCount > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {pendingCount} pending link{pendingCount === 1 ? "" : "s"} can
                    be checked for payment status
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    placeholder="Search by name, phone, description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleRefreshAll}
                  disabled={refreshingAll || !configured || pendingCount === 0}
                  className="gap-2"
                  title={
                    pendingCount === 0
                      ? "No pending links to refresh"
                      : "Poll Razorpay for all pending links"
                  }
                >
                  {refreshingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh statuses
                  {pendingCount > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {pendingCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchLinks}
                  disabled={loadingLinks}
                  title="Reload from database"
                >
                  <RefreshCw
                    className={cn(
                      "w-4 h-4",
                      loadingLinks && "animate-spin text-slate-500"
                    )}
                  />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLinks ? (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
                </div>
              ) : filteredLinks.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-500">
                  No payment links yet. Create one from the{" "}
                  <button
                    type="button"
                    className="text-[#0047FF] font-medium hover:underline"
                    onClick={() => setTab("create")}
                  >
                    Create Link
                  </button>{" "}
                  tab.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Link</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLinks.map((link) => (
                        <TableRow key={link.id}>
                          <TableCell className="whitespace-nowrap text-xs text-slate-500">
                            {link.created_at
                              ? format(new Date(link.created_at), "dd MMM yyyy, HH:mm")
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-900">
                                {link.customer_name || "—"}
                              </span>
                              <span className="text-xs text-slate-500">
                                {link.customer_phone || link.customer_email || ""}
                              </span>
                              {link.description && (
                                <span className="text-xs text-slate-400 mt-0.5 line-clamp-1">
                                  {link.description}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-medium">
                            <div className="flex flex-col">
                              <span>{formatAmount(link.amount, link.currency)}</span>
                              {Number(link.amount_paid) > 0 && (
                                <span className="text-[11px] text-emerald-600">
                                  Paid: {formatAmount(link.amount_paid, link.currency)}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium capitalize",
                                STATUS_STYLES[link.status] ||
                                  "bg-slate-100 text-slate-600 border-slate-200"
                              )}
                            >
                              {(link.status || "created").replace(/_/g, " ")}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[260px]">
                            {isSafeHttpsUrl(link.short_url) ? (
                              <a
                                href={link.short_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0047FF] text-xs font-medium hover:underline truncate block"
                              >
                                {link.short_url}
                              </a>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCopy(link.short_url)}
                                disabled={!isSafeHttpsUrl(link.short_url)}
                                title="Copy link"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  isSafeHttpsUrl(link.short_url) &&
                                  window.open(
                                    link.short_url,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                                disabled={!isSafeHttpsUrl(link.short_url)}
                                title="Open"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRefresh(link)}
                                disabled={refreshingId === link.id}
                                title="Refresh status"
                              >
                                <RefreshCw
                                  className={cn(
                                    "w-4 h-4",
                                    refreshingId === link.id && "animate-spin"
                                  )}
                                />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(link)}
                                title="Remove from history"
                              >
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RazorpayLinks;
